import { PRODUCT_NAME, consoleRoutes, defaultWelcome } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@sagot/ui";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { zodResolver } from "../../../lib/zod-resolver";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { UpgradeCard } from "../shared/plan-gate";
import type { WidgetOut } from "../shared/types";
import { useBillingOverview } from "../shared/use-shared-queries";
import { AgentNotLiveNotice, type AgentStatus } from "./agent-not-live-notice";
import { AppearancePanel } from "./appearance-panel";
import { BehaviorPanel } from "./behavior-panel";
import { ContentPanel } from "./content-panel";
import { InstallBadge } from "./install-badge";
import { InstallPanel } from "./install-panel";
import { HomePanel } from "./home-panel";
import { FLUSH, STUDIO_TITLE_ID } from "./layout";
import { PreviewPane } from "./preview-pane";
import { PublishChoiceDialog } from "./publish-choice-dialog";
import { SecurityPanel } from "./security-panel";
import { useDraftActions } from "./use-draft-actions";
import { WIDGET_TABS, isWidgetTab, toWidgetForm, widgetFormSchema, type WidgetForm, type WidgetTab } from "./widget-model";

const REMOVE_BRANDING = "remove_branding";
const K = "deployKnowledge.widgetStudio";

interface WidgetEditorProps {
  agentId: string;
  agentName: string;
  /** The agent's languages; the first picks the default greeting's language. */
  agentLanguages: readonly string[];
  /** `undefined` while the agent loads or when it is unreadable: no go-live notice is shown. */
  agentStatus: AgentStatus | undefined;
  widget: WidgetOut;
}

function useDomain(widget: WidgetOut): string | null {
  const seen = widget.install.last_seen_origin?.replace(/^https?:\/\//, "") ?? null;
  return seen ?? widget.security.allowed_domains[0] ?? null;
}

export function WidgetEditor({ agentId, agentName, agentLanguages, agentStatus, widget }: WidgetEditorProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const navigate = useNavigate();
  const param = useParams().tab;
  const tab: WidgetTab = isWidgetTab(param) ? param : "appearance";
  const permissions = usePermissions(agentId);
  const canEdit = permissions.can("widget.manage", { agentId }) && !permissions.readOnly;
  const billing = useBillingOverview();
  const brandingRemovable = billing.data?.features.includes(REMOVE_BRANDING) ?? false;
  const brandingLocked = billing.isSuccess && !brandingRemovable;
  const form = useForm<WidgetForm>({ resolver: zodResolver(widgetFormSchema), defaultValues: toWidgetForm(widget.draft), mode: "onChange" });
  const actions = useDraftActions({ agentId, widget, form, brandingLocked, agentStatus });
  const domain = useDomain(widget);
  const values = useWatch({ control: form.control }) as WidgetForm;
  const savedColor = toWidgetForm(widget.draft).appearance.primary_color;

  return (
    <FormProvider {...form}>
      <div className={`${FLUSH} flex flex-col lg:h-[calc(100vh-3.5rem)] lg:flex-row`}>
        <aside className="border-b border-line bg-surface lg:w-[420px] lg:shrink-0 lg:overflow-y-auto lg:border-b-0 lg:border-r" aria-labelledby={STUDIO_TITLE_ID}>
          <Tabs value={tab} onValueChange={(next) => void navigate(consoleRoutes.widgetStudio(summary.slug, agentId, next), { replace: true })}>
            <div className="sticky top-0 z-10 border-b border-line bg-surface px-5 pt-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h1 id={STUDIO_TITLE_ID} tabIndex={-1} className="text-2xl font-extrabold tracking-tight outline-none">
                    {t(`${K}.title`)}
                  </h1>
                  <p className="mt-1 truncate text-fg-muted">
                    {domain ? t(`${K}.subtitle`, { agent: agentName, domain }) : t(`${K}.subtitleNoDomain`, { agent: agentName })}
                  </p>
                </div>
                <InstallBadge installed={widget.install.installed} agentStatus={agentStatus} />
              </div>
              <TabsList className="-mx-1 mt-4 flex-wrap border-b-0" aria-label={t(`${K}.tabsLabel`)}>
                {WIDGET_TABS.map((value) => (
                  <TabsTrigger key={value} value={value} className="shrink-0 whitespace-nowrap px-2.5 text-[13px]">
                    {t(`${K}.tabs.${value}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <div className="space-y-6 p-5">
              <AgentNotLiveNotice agentId={agentId} status={agentStatus} />
              {actions.planGated ? <UpgradeCard feature={t(`${K}.appearance.brandingFeature`, { product: PRODUCT_NAME })} /> : null}
              <fieldset disabled={!canEdit} className="min-w-0">
                <TabsContent value="appearance" forceMount className="data-[state=inactive]:hidden">
                  <AppearancePanel agentId={agentId} agentName={agentName} savedColor={savedColor} serverWarning={widget.contrast.warning} brandingRemovable={brandingRemovable} disabled={!canEdit} />
                </TabsContent>
                <TabsContent value="content" forceMount className="data-[state=inactive]:hidden">
                  <ContentPanel />
                </TabsContent>
                <TabsContent value="home" forceMount className="data-[state=inactive]:hidden">
                  <HomePanel agentId={agentId} widget={widget} active={tab === "home"} />
                </TabsContent>
                <TabsContent value="behavior" forceMount className="data-[state=inactive]:hidden">
                  <BehaviorPanel disabled={!canEdit} />
                </TabsContent>
              </fieldset>
              <TabsContent value="install" forceMount className="data-[state=inactive]:hidden">
                <InstallPanel agentId={agentId} install={widget.install} />
              </TabsContent>
              <TabsContent value="security" forceMount className="data-[state=inactive]:hidden">
                <SecurityPanel agentId={agentId} widget={widget} disabled={!canEdit} />
              </TabsContent>
            </div>
          </Tabs>
        </aside>
        <PreviewPane
          tab={tab}
          agentId={agentId}
          form={values}
          agentName={agentName}
          defaultGreeting={defaultWelcome({ assistantName: agentName, orgName: summary.name, languages: agentLanguages })}
          siteName={summary.name}
          domain={domain}
          widget={widget}
          canEdit={canEdit}
          dirty={form.formState.isDirty}
          saving={actions.saving}
          publishing={actions.publishing}
          onSave={() => void actions.saveDraft()}
          onPublish={() => void actions.requestPublish()}
        />
        <PublishChoiceDialog open={actions.choiceOpen} onOpenChange={actions.setChoiceOpen} onChoose={(scope) => void actions.choosePublishScope(scope)} pending={actions.pendingScope} />
      </div>
    </FormProvider>
  );
}
